/**
 * Route metadata: the page title behind every SPA path.
 *
 * Source: docs/product/experience-and-ui/43-cross-cutting-ui-patterns.md
 * § Accessibility (page title, focus on navigation) — ENG-APP-UX-TUNEUP
 * Track A (floor row 1: 53 / 53 routes rendered the bare wordmark as
 * `document.title`; WCAG 2.2 § 2.4.2 Page Titled).
 *
 * Pure: a path → i18n key table resolved with react-router's `matchPath`
 * (first rule wins, so specific patterns precede their parameterised
 * siblings). The key is EITHER a `pageTitles.*` entry (the title namespace
 * every catalog carries) OR the existing label the page already renders
 * (settings sub-pages, asset tabs) — one vocabulary, never a second copy of
 * a label. `usePageTitle` resolves the key through `t()` and writes
 * `<Page> · ASOLOOP`; `Layout` reads the same title for the route-change
 * announcement. `verify_sidebar_accordion_runtime.py` pins that every key
 * below resolves in `en.json` (the catalog parity verify carries it to the
 * other locales).
 */
import { matchPath } from "react-router-dom";

type Params = Record<string, string | undefined>;

export interface RouteTitleRule {
  /** react-router path pattern, matched with `end: true`. */
  readonly pattern: string;
  /** An i18n key, or a resolver over the matched params. */
  readonly key: string | ((params: Params) => string);
}

/** The brand suffix every document title ends with. */
export const DOCUMENT_TITLE_BRAND = "ASOLOOP";
/** The separator between the page title and the brand. */
export const DOCUMENT_TITLE_SEPARATOR = " · ";

/** `/settings/:subpage` + `/app/:subpage` → the sub-page's own label. */
const SETTINGS_SUBPAGE_TITLE_KEYS: Readonly<Record<string, string>> = {
  account: "pages.settingsHub.subPages.account.label",
  "members-billing": "pages.settingsHub.subPages.membersBilling.label",
  billing: "pages.settingsHub.subPages.billing.label",
  notifications: "pages.settingsHub.subPages.notifications.label",
  "app-information": "pages.settingsHub.subPages.appInformation.label",
  "app-basics": "pages.settingsHub.subPages.appInformation.label",
  integrations: "pages.settingsHub.subPages.integrations.label",
  "store-connections": "pages.settingsHub.subPages.storeConnections.label",
  "ai-settings": "pages.settingsHub.subPages.aiSettings.label",
  "ai-rules": "pages.settingsHub.subPages.aiSettings.label",
  "ai-behavior": "pages.settingsHub.subPages.aiSettings.label",
  usage: "pages.settingsHub.subPages.usage.label",
  reports: "pages.settingsHub.subPages.reports.label",
  "executive-dashboard": "pages.settingsHub.subPages.executiveDashboard.label",
  "api-keys": "pages.settingsHub.subPages.apiKeys.label",
  "audit-log": "pages.settingsHub.subPages.auditLog.label",
};

/** `/creatives/:tab` → the tab's own label (the Asset Library h1 is shared). */
const CREATIVES_TAB_TITLE_KEYS: Readonly<Record<string, string>> = {
  screenshots: "pages.creatives.tabScreenshots",
  videos: "pages.creatives.tabVideos",
  icons: "pages.creatives.tabIcons",
  copy: "pages.creatives.tabCopy",
  cpp: "pages.creatives.tabCustomProductPages",
  csl: "pages.creatives.tabCustomStoreListings",
  "content-rules": "pages.creatives.tabContentRules",
  generate: "pages.creatives.generatePageTitle",
};

const settingsTitle = (params: Params): string =>
  SETTINGS_SUBPAGE_TITLE_KEYS[params.subpage ?? ""] ?? "pageTitles.settings";

const creativesTitle = (params: Params): string =>
  CREATIVES_TAB_TITLE_KEYS[params.tab ?? ""] ?? "pages.creatives.pageTitle";

/**
 * Ordered rules — one per registered route family in `App.tsx`. Redirect-only
 * paths (`/signals`, `/learnings/*`, `/experiments/decisions`, `/programs`,
 * `/creatives`, `/app`, `/settings`, …) are NOT listed: the title resolves on
 * the path the redirect lands on.
 */
export const ROUTE_TITLE_RULES: readonly RouteTitleRule[] = [
  { pattern: "/", key: "pageTitles.dashboard" },
  // Public + auth
  { pattern: "/login", key: "pageTitles.login" },
  { pattern: "/signup", key: "pageTitles.signup" },
  { pattern: "/forgot-password", key: "pageTitles.forgotPassword" },
  { pattern: "/reset-password", key: "pageTitles.resetPassword" },
  { pattern: "/verify-email", key: "pageTitles.verifyEmail" },
  { pattern: "/magic-link", key: "pageTitles.magicLink" },
  { pattern: "/magic-link/verify", key: "pageTitles.magicLinkVerify" },
  { pattern: "/invitations/accept", key: "pageTitles.invitationAccept" },
  { pattern: "/dashboard/:workspaceToken", key: "pageTitles.publicDashboard" },
  { pattern: "/admin/login", key: "pageTitles.adminLogin" },
  { pattern: "/admin/set-password", key: "pageTitles.adminSetPassword" },
  { pattern: "/admin/costs", key: "pageTitles.adminCosts" },
  // Onboarding
  { pattern: "/onboarding", key: "pageTitles.onboarding" },
  { pattern: "/onboarding/plan", key: "pageTitles.onboardingPlan" },
  {
    pattern: "/onboarding/connect-app",
    key: "pageTitles.onboardingConnectApp",
  },
  { pattern: "/onboarding/blocked", key: "pageTitles.onboardingBlocked" },
  // Recommendations
  { pattern: "/recommendations", key: "pages.recommendations.title" },
  {
    pattern: "/recommendations/:hypothesisId",
    key: "pageTitles.hypothesisDetail",
  },
  // Experiments
  { pattern: "/experiments/active", key: "pageTitles.experimentsActive" },
  { pattern: "/experiments/history", key: "pageTitles.experimentsHistory" },
  { pattern: "/experiments/new", key: "pageTitles.experimentsNew" },
  { pattern: "/experiments/:state/:id", key: "pageTitles.experimentDetail" },
  // Workflows (programs)
  { pattern: "/programs/active", key: "pageTitles.programsActive" },
  { pattern: "/programs/templates", key: "pageTitles.programsTemplates" },
  { pattern: "/programs/new", key: "pageTitles.programsNew" },
  { pattern: "/programs/:programId", key: "pageTitles.programDetail" },
  {
    pattern: "/reports/cross-app-executive-progress",
    key: "pages.crossAppExecReport.title",
  },
  // Analytics (evidence)
  { pattern: "/evidence/signals", key: "pages.signals.title" },
  { pattern: "/evidence/hypotheses", key: "pages.hypotheses.title" },
  { pattern: "/evidence/signal-sources", key: "pages.signalSources.title" },
  { pattern: "/evidence/reports", key: "pages.reports.title" },
  { pattern: "/evidence/targeting-pages", key: "pages.targetingPages.title" },
  { pattern: "/evidence/keyword-metrics", key: "pages.keywordMetrics.title" },
  // Assets (creatives)
  { pattern: "/creatives/:tab", key: creativesTitle },
  // App configuration + Settings
  { pattern: "/app/:subpage", key: settingsTitle },
  { pattern: "/settings/:subpage", key: settingsTitle },
  { pattern: "/team", key: "pages.team.title" },
  // Engineering scaffolding
  { pattern: "/_eng/*", key: "pageTitles.engineering" },
];

/** The i18n key of the page title for `pathname`; `pageTitles.notFound` when no rule matches. */
export function resolvePageTitleKey(pathname: string): string {
  for (const rule of ROUTE_TITLE_RULES) {
    const match = matchPath({ path: rule.pattern, end: true }, pathname);
    if (match) {
      return typeof rule.key === "function" ? rule.key(match.params) : rule.key;
    }
  }
  return "pageTitles.notFound";
}

/** `<Page> · ASOLOOP` — the one document-title shape (floor row 1). */
export function composeDocumentTitle(pageTitle: string): string {
  return `${pageTitle}${DOCUMENT_TITLE_SEPARATOR}${DOCUMENT_TITLE_BRAND}`;
}
